import { FilterDefinition, SelectedFilter } from '../api/dashboard';

/**
 * `<input type="date">` yields YYYY-MM-DD. `new Date("2020-01-01")` parses that
 * as UTC midnight, which renders as the previous day in US timezones, so build
 * the date from its parts instead.
 */
function formatDate(value: string): string {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!parts) {
        return value;
    }
    return new Date(
        Number(parts[1]),
        Number(parts[2]) - 1,
        Number(parts[3])
    ).toLocaleDateString();
}

/**
 * Human-readable description of what is currently filtered, for the toast.
 * e.g. "Filtered to Sunday", "Filtered to Sunday, 1/1/2020 - 12/31/2024",
 * or "Showing all incidents" when nothing is applied.
 */
export function buildFilterSummary(
    selected: SelectedFilter[],
    filterDefinitions: FilterDefinition[] | null
): string {
    const parts: string[] = [];

    selected.forEach((filter) => {
        // A filter row that has been added but not yet given a value does not
        // narrow anything, so it does not belong in the summary.
        if (!filter.value || filter.name === 'StartDate' || filter.name === 'EndDate') {
            return;
        }
        const definition = filterDefinitions?.find((d) => d.id === filter.name);
        // Values are self-describing ("Sunday"), so lead with the value and
        // only fall back to the definition name if we cannot resolve one.
        parts.push(definition ? filter.value : `${filter.name}: ${filter.value}`);
    });

    const start = selected.find((f) => f.name === 'StartDate' && f.value);
    const end = selected.find((f) => f.name === 'EndDate' && f.value);
    if (start && end) {
        parts.push(`${formatDate(start.value)} - ${formatDate(end.value)}`);
    } else if (start) {
        // Trailing rather than leading ("1/1/2020 or later", not "on or after
        // 1/1/2020") so the phrase reads correctly whether or not it follows
        // other filters.
        parts.push(`${formatDate(start.value)} or later`);
    } else if (end) {
        parts.push(`${formatDate(end.value)} or earlier`);
    }

    return parts.length > 0 ? `Filtered to ${parts.join(', ')}` : 'Showing all incidents';
}

export function formatFatalities(total: number): string {
    return `${total.toLocaleString()} ${total === 1 ? 'fatality' : 'fatalities'}`;
}
